/** Public Map — Leaflet markers for events, places, activities. */
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { fetchMapMarkers } from '@/services/api/public';
import { PUBLIC_IDS } from '@/constants/testIds';

const PORTO = [43.2606, 13.7622];
const TYPE_PATH = { event: 'eventi', place: 'luoghi', activity: 'attivita' };
const TYPE_COLOR = { event: '#34d399', place: '#38bdf8', activity: '#f59e0b' };

function pin(color) {
	return L.divIcon({
		className: '',
		html: `<div style="width:18px;height:18px;border-radius:50% 50% 50% 0;background:${color};transform:rotate(-45deg);border:2px solid #0b1120;box-shadow:0 0 0 2px ${color}55"></div>`,
		iconSize: [18, 18], iconAnchor: [9, 18],
	});
}

export default function PublicMap() {
	const { data } = useQuery({ queryKey: ['pub-map'], queryFn: fetchMapMarkers });
	const markers = data?.markers || [];
	const el = useRef(null);
	const mapRef = useRef(null);
	const layerRef = useRef(null);
	const navigate = useNavigate();
	const [located, setLocated] = useState(false);

	useEffect(() => {
		if (mapRef.current || !el.current) return;
		mapRef.current = L.map(el.current).setView(PORTO, 14);
		L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
			attribution: '&copy; OpenStreetMap', maxZoom: 19,
		}).addTo(mapRef.current);
		layerRef.current = L.layerGroup().addTo(mapRef.current);
	}, []);

	useEffect(() => {
		if (!layerRef.current) return;
		layerRef.current.clearLayers();
		markers.forEach((m) => {
			const mk = L.marker([m.lat, m.lng], { icon: pin(TYPE_COLOR[m.type] || '#34d399') }).addTo(layerRef.current);
			const cat = m.category ? `<div style="color:#94a3b8;font-size:11px;margin-top:2px">${m.category}</div>` : '';
			mk.bindPopup(`<strong>${m.title}</strong>${cat}<br/><a href="#/${TYPE_PATH[m.type]}/${m.slug}" style="color:#059669">Apri scheda →</a>`);
			mk.on('popupopen', () => {
				const a = document.querySelector('.leaflet-popup-content a');
				if (a) a.onclick = (e) => { e.preventDefault(); navigate(`/${TYPE_PATH[m.type]}/${m.slug}`); };
			});
		});
	}, [markers, navigate]);

	const locate = () => {
		if (!navigator.geolocation || !mapRef.current) return;
		navigator.geolocation.getCurrentPosition(
			(pos) => { mapRef.current.setView([pos.coords.latitude, pos.coords.longitude], 15); setLocated(true); },
			() => setLocated(false)
		);
	};

	return (
		<div className="mx-auto max-w-6xl px-5 py-10">
			<div className="mb-4 flex items-center justify-between">
				<h1 className="text-3xl font-extrabold" style={{ fontFamily: "'Poppins', sans-serif" }}>Mappa</h1>
				<button onClick={locate} className="rounded-full border border-slate-700 px-4 py-1.5 text-sm text-slate-300 hover:border-emerald-400/40 hover:text-emerald-300">
					{located ? 'Posizione attiva' : 'La mia posizione'}
				</button>
			</div>
			<div data-testid={PUBLIC_IDS.map} ref={el} className="h-[70vh] w-full overflow-hidden rounded-2xl border border-slate-800" />
			<div className="mt-3 flex gap-4 text-xs text-slate-400">
				<span><span className="mr-1 inline-block h-2 w-2 rounded-full" style={{ background: TYPE_COLOR.event }} />Eventi</span>
				<span><span className="mr-1 inline-block h-2 w-2 rounded-full" style={{ background: TYPE_COLOR.place }} />Luoghi</span>
				<span><span className="mr-1 inline-block h-2 w-2 rounded-full" style={{ background: TYPE_COLOR.activity }} />Attività</span>
			</div>
		</div>
	);
}
